import React, { useEffect, useRef, useState } from 'react';
import { Button, Drawer, Grid, Paper, Typography, useMediaQuery } from '@mui/material';
import { useDocumentListInfinite, usePreloadBulletinYears, usePreloadDocumentList, usePreloadDocumentListCounts } from '../../fetch/fetchDocuments';
import DocumentListItemIcon from './DocumentListItemIcon';
import DocumentListItemDetails from './DocumentListItemDetails';
import DocumentListFilters from './filters/DocumentListFilters';
import {
    useIsInViewport, usePhoneMode, useCheckForPassedInFamilyFilter
} from '../../hooks';
import { useSelector } from 'react-redux';
import Spinner from '../spinner/Spinner';
import { storageHelper } from '../../helpers';
import { useTheme } from '@emotion/react';
import ScrollToTop from './ScrollToTop';
import dayjs from 'dayjs';
import ServiceBulletinYearHeader from './ServiceBulletinYearHeader';
import DocumentListScrollView from './DocumentListScrollView';
import DocumentListSubMenu from './subMenu/DocumentListSubMenu';
import SerialNumberMatchNode from './SerialNumberMatchNote';
import { preloadDocumentTypeList } from '../../fetch/fetchClassificationItems';
import DocumentListTable from './DocumentListTable';

export default function DocumentList() {
    const theme = useTheme();
    const { families, type: documentType } = useSelector((state) => state.libraryFilters)
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    useCheckForPassedInFamilyFilter();

    useEffect(() => {
        document.title = 'Mercury Literature';
    }, []);

    preloadDocumentTypeList();
    usePreloadBulletinYears();
    usePreloadDocumentListCounts();

    const { phoneMode } = usePhoneMode();

    const serialNumberMatchRef = useRef(null);

    const useFloatingFilters = useMediaQuery(theme.breakpoints.down("md"));

    const storedListView = storageHelper.getListView();
    const [stateListView, setListView] = useState(storedListView);
    const listView = documentType != '2,9,10' ? stateListView : 'list';

    const storedTextOnlyMode = storageHelper.getDetailsTextOnly();
    const [textOnlyMode, setTextOnlyMode] = useState(storedTextOnlyMode);

    const { documents, size, totalRecords: requestTotalRecords, setSize, isLoading, isValidating, serialNumberMatch } = useDocumentListInfinite(listView);

    const loadMoreRef = useRef(null);
    const [isInViewport1, _intersectionRatio] = useIsInViewport(loadMoreRef);
    useEffect(() => {
        if (isInViewport1 && !isLoading && !isValidating && documents.length < requestTotalRecords) {
            setTimeout(() => {
                setSize(size + 1);
            }, 250);
        }
    }, [isInViewport1, isLoading, isValidating, documents.length, requestTotalRecords]);

    usePreloadDocumentList(size, !isLoading && !isValidating && documents.length < requestTotalRecords);

    let containerColumns = { xs: 2, sm: 3, md: 4, lg: 4, xl: 6 };
    if (filterDrawerOpen) {
        containerColumns = { ...containerColumns, md: 2, lg: 3, xl: 5 };
    }
    if (listView === 'medium') {
        containerColumns = { xs: 3, sm: 4, md: 6, lg: 6, xl: 8 };
        if (filterDrawerOpen) {
            containerColumns = { ...containerColumns, md: 4, lg: 5, xl: 7 };
        }
    } else if (listView == 'list') {
        containerColumns = { xs: 1 };
    }

    const getGroupingType = (type) => {
        // We want to group Service Manuals (1) and Diagnostic Manuals (5) together
        if (type == 5 || type == 6) {
            return 1;
        }

        // Group Advisories with bulletins
        if (type == 9 || type == 10) {
            return 2;
        }

        return type;
    }

    const areTypesDifferentForGrouping = (type1, type2) => {
        return getGroupingType(type1) != getGroupingType(type2);
    }

    return (
        <>
            <ScrollToTop />
            {useFloatingFilters ? (
                <Drawer
                    anchor='left'
                    open={filterDrawerOpen}
                    onClose={() => setFilterDrawerOpen(false)}
                >
                    <DocumentListFilters
                        onCloseClick={() => setFilterDrawerOpen(false)}
                        isFloating
                    />
                </Drawer>
            ) : null}
            <DocumentListSubMenu
                filtersOpen={filterDrawerOpen}
                onToggleOpenClick={() => setFilterDrawerOpen(!filterDrawerOpen)}
                stateListView={stateListView}
                setListView={setListView}
                textOnlyMode={textOnlyMode}
                onToggleTextOnlyMode={(newValue) => {
                    setTextOnlyMode(newValue);
                    storageHelper.setDetailsTextOnly(newValue);
                }}
            />
            <DocumentListScrollView hideScroll={textOnlyMode && listView === 'list'}>
                {!isLoading && requestTotalRecords == 0 && (!textOnlyMode || listView !== 'list') ? (<Grid item xs={12}>
                    <div style={{ textAlign: 'center', color: '#fff' }}>
                        <Typography variant="h4">
                            No Results
                        </Typography>
                    </div>
                </Grid>) : null}
                {filterDrawerOpen && !useFloatingFilters ? (
                    <Grid item xs='auto' style={{ minWidth: 380 }}>
                        <Paper
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'fixed',
                                top: theme.appBar.height + theme.appBar.buffer,
                                maxHeight: 'calc(100% - 5em)',
                            }}
                        >
                            <div style={{
                                height: '100%',
                                width: '100%',
                                overflow: 'hidden'
                            }}>
                                <DocumentListFilters
                                    onCloseClick={() => setFilterDrawerOpen(false)}
                                />
                            </div>
                        </Paper>
                    </Grid>
                ) : null}
                <Grid item xs>
                    {serialNumberMatch != null ? (
                        <Grid container spacing={2} style={{ paddingBottom: '1em' }} ref={serialNumberMatchRef}>
                            {serialNumberMatch.map((serialMatch, index) => {
                                return (
                                    <SerialNumberMatchNode key={index} serialMatch={serialMatch} />
                                )
                            })}
                        </Grid>
                    ) : null}
                    {!textOnlyMode || listView !== 'list' ? (
                        <Grid container columns={containerColumns} spacing={2}>
                            {documents.map((document, index) => {
                                const { hasRead, releaseDate, modificationTimestamp } = document;
                                const isNew = !hasRead && !!releaseDate && dayjs().diff(dayjs(releaseDate || modificationTimestamp), 'day') < 30;

                                if (listView === 'list') {
                                    return (
                                        <React.Fragment key={document.partNumber}>
                                            {documentType == '2,9,10' && (
                                                index == 0 ||
                                                (index > 0 && document.year != documents[index - 1].year)
                                            ) ? (
                                                <ServiceBulletinYearHeader year={document.year} />
                                            ) : null}
                                            <DocumentListItemDetails isNew={isNew} document={document} small={documentType == '2,9,10'} textOnlyMode={textOnlyMode} />
                                        </React.Fragment>
                                    );
                                } else {
                                    return (
                                        <React.Fragment key={document.partNumber}>
                                            {!phoneMode && !!families && families.length > 0 &&
                                                index > 0 && areTypesDifferentForGrouping(document.documentTypeId, documents[index - 1].documentTypeId) ? (
                                                <Grid item xs={12}></Grid>
                                            ) : null}
                                            <DocumentListItemIcon key={document.partNumber} isNew={isNew} document={document} stateListView={stateListView} />
                                        </React.Fragment>
                                    );
                                }
                            })}
                        </Grid>
                    ) : (
                        <DocumentListTable
                            documents={documents}
                            serialMatch={serialNumberMatch != null}
                            loadMoreVisible={documents.length < requestTotalRecords}
                            notLoading={!isLoading && !isValidating}
                            onLoadMore={() => setSize(size + 1)}
                            requestTotalRecords={requestTotalRecords}
                            serialNoteHeight={serialNumberMatchRef?.current?.clientHeight || 0}
                        />
                    )}
                </Grid>
                {isLoading ? (<Grid item xs={12}>
                    <div style={{ position: 'relative', left: 'calc(50% - 30px)' }}>
                        <Spinner />
                    </div>
                </Grid>) : null}
                <Grid item xs={12}
                    style={{ display: documents.length < requestTotalRecords && (!textOnlyMode || listView !== 'list') ? null : 'none' }}
                    ref={loadMoreRef}>
                    <Paper
                        sx={{
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                        }}
                    >
                        {!isLoading && !isValidating ? (
                            <Button
                                onClick={() => setSize(size + 1)}
                            >
                                Load Next
                            </Button>
                        ) : (
                            <div style={{ position: 'relative', left: 'calc(50% - 30px)' }}>
                                <Spinner />
                            </div>
                        )}
                    </Paper>
                </Grid>
            </DocumentListScrollView>
        </>
    );
}